import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as apiSettings from 'api/apiSettings';
import { useToast } from 'context/ToastContext';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { mapStatusResponse } from './helpers';
import type { TelegramBotStatus } from './types';

const useTelegramBot = () => {
    const { t } = useTranslation(['Settings', 'common']);
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const [token, setToken] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [status, setStatus] = useState<TelegramBotStatus | null>(null);

    const applyStatus = (resStatus: Record<string, unknown> | null | undefined) => {
        setStatus(mapStatusResponse(resStatus));
    };

    const loadStatus = async () => {
        setIsLoading(true);
        const resStatus = await apiSettings.getTelegramBotStatus();
        if (!resStatus) {
            showToast(t('common:messError'), 'danger');
            setIsLoading(false);
            return;
        }
        applyStatus(resStatus);
        setToken('');
        setIsLoading(false);
    };

    const saveToken = async () => {
        setIsSaving(true);
        const result = await apiSettings.setTelegramBotToken(token);
        if (!result?.stat) {
            showToast(t('telegramBot.errors.saveFailed'), 'danger');
            setIsSaving(false);
            return;
        }
        applyStatus(result);
        showToast(
            result?.isWebhookActive ? t('telegramBot.toasts.active') : t('telegramBot.toasts.saved'),
            result?.isWebhookActive ? 'success' : 'warning',
        );
        setIsSaving(false);
    };

    const deactivateBot = async () => {
        setIsSaving(true);
        const result = await apiSettings.deactivateTelegramBot();
        if (!result?.stat) {
            showToast(t('telegramBot.errors.deactivateFailed'), 'danger');
            setIsSaving(false);
            return;
        }
        applyStatus(result);
        setToken('');
        showToast(t('telegramBot.toasts.deactivated'), 'success');
        setIsSaving(false);
    };

    const askDeactivateBot = () => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[0].handleClick = () => showDlg(false);
        buttons[1].handleClick = () => {
            showDlg(false);
            deactivateBot();
        };
        showDlg({
            type: 'confirm',
            title: t('telegramBot.deactivate.confirmTitle'),
            text: t('telegramBot.deactivate.confirmText'),
            buttons,
        });
    };

    useEffect(() => {
        loadStatus();
    }, []);

    return {
        token,
        setToken,
        isLoading,
        isSaving,
        status,
        loadStatus,
        saveToken,
        askDeactivateBot,
    };
};

export default useTelegramBot;
